import { lazy, Suspense, useCallback, useEffect, useRef, useState, type PropsWithChildren } from "react";
import { Alert, Box, Button, Checkbox, CircularProgress, Dialog, DialogContent, DialogTitle, FormControlLabel, Link, Stack, Typography } from "@mui/material";
import { useLocation } from "react-router-dom";
import { useAuthContext } from "src/auth/hooks";
import { useTranslate } from "src/locales";
import { communicationCommand, usePendingLegalDocuments } from "src/api/federation-communication";
import uuidv4 from "src/utils/uuidv4";
import { isPendingLegalDocumentsResponse } from "src/utils/pending-legal-documents";
import { LoadingScreen } from "src/components/loading-screen";
import LegalAcceptanceActions, { legalDialogPaperSx } from "./legal-acceptance-actions";

const AccountDelete = lazy(() => import("src/sections/player/player-profiledata-delete-profile"));
const recoveryPath = /^\/(privacy-policy|terms-and-conditions|auth\/jwt\/(forgot-password|new-password))(\/|$)/;

export default function LegalAcceptanceGate({ children }: PropsWithChildren) {
  const { user, logout } = useAuthContext();
  const { pathname } = useLocation();
  const { t } = useTranslate();
  const pending = usePendingLegalDocuments();
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [failed, setFailed] = useState(false);
  const [privacy, setPrivacy] = useState(false);
  const command = useRef(uuidv4());
  const savingLock = useRef(false);
  const documents = isPendingLegalDocumentsResponse(pending.data)
    ? pending.data.items
    : undefined;
  const document = user?.id ? documents?.[0] : undefined;
  const unavailable = Boolean(pending.error || (!pending.isLoading && !documents));
  const refreshPending = useCallback(async () => {
    try { await pending.mutate(); }
    catch { /* SWR exposes the error in the blocking retry state. */ }
  }, [pending.mutate]);
  useEffect(() => { setConfirmed(false); setFailed(false); setPrivacy(false); command.current = uuidv4(); }, [user?.id, document?.id, document?.checksum]);
  useEffect(() => {
    const refresh = () => { void refreshPending(); };
    window.addEventListener("legal-acceptance-required", refresh);
    return () => window.removeEventListener("legal-acceptance-required", refresh);
  }, [refreshPending]);
  if (!user?.id || recoveryPath.test(pathname)) return <>{children}</>;
  const blocked = Boolean(document || unavailable || pending.isLoading || !documents);
  if (!blocked) return <>{children}</>;
  // FS-2026-030/036: checking the account does not mean acceptance is required.
  // Keep content gated without mounting a consent dialog before the result.
  if (!document && !unavailable && pending.isLoading) {
    return <LoadingScreen sx={{ minHeight: "100vh" }} role="status" aria-label={t("loading")} />;
  }
  const accept = async () => {
    if (!document || !confirmed || savingLock.current || unavailable) return;
    savingLock.current = true; setSaving(true); setFailed(false);
    try {
      await communicationCommand("/me/federation/legal/accept", { command_id: command.current, version_id: document.id,
        checksum: document.checksum, capacity: document.guardian_required ? "guardian" : "self", confirmed: true });
      await refreshPending();
      setConfirmed(false);
    } catch { setFailed(true); await refreshPending(); }
    finally { savingLock.current = false; setSaving(false); }
  };
  return <Dialog open fullWidth maxWidth="md" disableEscapeKeyDown aria-labelledby="legal-acceptance-title" PaperProps={{ sx: legalDialogPaperSx }}>
    <DialogTitle id="legal-acceptance-title">{t(document ? "comm_legal_required" : "comm_error")}</DialogTitle>
    <DialogContent dividers sx={{ minHeight: 0 }}>
      <Stack spacing={2}>
        {(unavailable || failed) && <Alert severity="error" action={<Button onClick={() => { void refreshPending(); }}>{t("retry")}</Button>}>{t("comm_error")}</Alert>}
        {pending.isLoading && <CircularProgress aria-label={t("comm_loading")} />}
        {document && <>
          <Typography variant="subtitle1">{document.provider_name} — {document.title} · {t("comm_version")} {document.version}</Typography>
          <Alert severity="info">{document.notice}</Alert>
          <Box sx={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere", maxHeight: { xs: "35vh", md: "45vh" }, overflow: "auto", p: 2, bgcolor: "background.neutral" }} tabIndex={0} role="region" aria-label={document.title}>{document.body}</Box>
          <Typography variant="body2">{t("comm_remaining", { count: documents?.length ?? 0 })}</Typography>
        </>}
        <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap">
          <Link href="/privacy-policy">{t("privacy_policy")}</Link>
          <Button size="small" onClick={() => setPrivacy(!privacy)}>{t("comm_account_options")}</Button>
        </Stack>
        {privacy && <Suspense fallback={<CircularProgress />}><AccountDelete /></Suspense>}
      </Stack>
    </DialogContent>
    <LegalAcceptanceActions confirmations={document ? <FormControlLabel control={<Checkbox checked={confirmed} onChange={(_, value) => setConfirmed(value)} disabled={saving} />}
      label={t(document.guardian_required ? "comm_confirm_guardian" : "comm_confirm_self")} /> : undefined}>
      <Button onClick={() => { void logout(); }} disabled={saving}>{t("playermenu_logout")}</Button>
      <Button variant="contained" onClick={() => { void accept(); }} disabled={!confirmed || saving || !document || unavailable}>{t("comm_accept")}</Button>
    </LegalAcceptanceActions>
  </Dialog>;
}
